import React, { useState, useEffect } from 'react';
import { History } from '../types';
import { historyAPI } from '../services/api';

const getActionColor = (action: string): string => {
  const a = action.toLowerCase();
  if (a.includes('login')) return '#2563eb';
  if (a.includes('create')) return '#059669';
  if (a.includes('update')) return '#d97706';
  if (a.includes('delete')) return '#dc2626';
  if (a.includes('fix')) return '#7c3aed';
  if (a.includes('bulk')) return '#0891b2';
  if (a.includes('comment')) return '#6366f1';
  return '#6b7280';
};

const getActionIcon = (action: string): string => {
  const a = action.toLowerCase();
  if (a.includes('login')) return '🔐';
  if (a.includes('delete')) return '🗑️';
  if (a.includes('create')) return '➕';
  if (a.includes('update')) return '✏️';
  if (a.includes('fix')) return '🔧';
  if (a.includes('bulk')) return '📦';
  if (a.includes('comment')) return '💬';
  return '📋';
};

const timeAgo = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
};

const HistoryComponent: React.FC = () => {
  const [history, setHistory] = useState<History[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<History | null>(null);

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try {
      const response = await historyAPI.getAll();
      setHistory(response.data);
    } catch (error) { console.error('Error fetching history:', error); }
    finally { setLoading(false); }
  };

  const filteredHistory = history.filter(entry =>
    !filter || [entry.username, entry.action, entry.entity_type, entry.details || ''].some(f => f.toLowerCase().includes(filter.toLowerCase()))
  );

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid #e5e7eb', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading history...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    </div>
  );

  const actionTypes = Array.from(new Set(history.map(h => h.action)));

  return (
    <div style={{ padding: '28px 32px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0, color: '#111827' }}>Activity History</h1>
          <p style={{ color: '#6b7280', margin: '6px 0 0 0', fontSize: '14px' }}>{history.length} total entries</p>
        </div>
        <button onClick={fetchHistory} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#374151' }}>
          ⟳ Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {actionTypes.slice(0, 5).map(action => (
          <div key={action} style={{ background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '16px' }}>{getActionIcon(action)}</span>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>{action}</span>
            </div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: getActionColor(action) }}>
              {history.filter(h => h.action === action).length}
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="🔍  Search by user, action, or details..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: '100%', maxWidth: '400px', padding: '10px 16px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '14px', background: '#fafbfc', outline: 'none' }}
        />
      </div>

      {/* Activity List */}
      <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #f0f0f0', overflow: 'hidden' }}>
        {filteredHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
            <p style={{ fontSize: '14px' }}>No entries found</p>
          </div>
        ) : (
          filteredHistory.map((entry, idx) => (
            <div
              key={entry.id}
              onClick={() => setSelectedEntry(entry)}
              style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', borderBottom: idx < filteredHistory.length - 1 ? '1px solid #f9fafb' : 'none', transition: 'background 0.1s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#fafbfc'; }}
              onMouseLeave={e => { e.currentTarget.style.background = ''; }}
            >
              {/* Icon */}
              <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: `${getActionColor(entry.action)}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', flexShrink: 0 }}>
                {getActionIcon(entry.action)}
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, background: `${getActionColor(entry.action)}15`, color: getActionColor(entry.action) }}>
                    {entry.action}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{entry.username}</span>
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>· {entry.entity_type}</span>
                </div>
                {entry.details && (
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.details}
                  </div>
                )}
              </div>

              {/* Time */}
              <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 500, flexShrink: 0, textAlign: 'right' }}>
                {timeAgo(entry.timestamp!)}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detail Modal */}
      {selectedEntry && (
        <div onClick={() => setSelectedEntry(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', maxWidth: '520px', width: '100%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: `${getActionColor(selectedEntry.action)}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                  {getActionIcon(selectedEntry.action)}
                </div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111827' }}>Activity Detail</h3>
              </div>
              <button onClick={() => setSelectedEntry(null)} style={{ width: '32px', height: '32px', borderRadius: '8px', border: 'none', background: '#f3f4f6', cursor: 'pointer', fontSize: '16px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div style={{ padding: '24px', display: 'grid', gap: '16px' }}>
              {[
                { label: 'Action', value: selectedEntry.action },
                { label: 'User', value: selectedEntry.username },
                { label: 'Entity Type', value: selectedEntry.entity_type },
                { label: 'Time', value: new Date(selectedEntry.timestamp!).toLocaleString('en-NZ') },
              ].map((field, i) => (
                <div key={i}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{field.label}</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: field.label === 'Action' ? 600 : 400 }}>{field.value}</div>
                </div>
              ))}
              {selectedEntry.details && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Details</div>
                  <div style={{ fontSize: '13px', color: '#374151', padding: '12px', background: '#f9fafb', borderRadius: '8px', lineHeight: 1.5 }}>{selectedEntry.details}</div>
                </div>
              )}
              {selectedEntry.old_values && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Changes</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', padding: '12px', background: '#f9fafb', borderRadius: '8px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '200px', overflow: 'auto' }}>
                    {selectedEntry.old_values}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryComponent;
